import {
  TCustomerModelJSON,
  TCampaignModelJSON
} from "@shoutout-labs/market_buzz_crm_types";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import weekOfYear from "dayjs/plugin/weekOfYear";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);
dayjs.extend(weekOfYear);
dayjs.locale("en-gb");

export const getOrdinalSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) return `${day}th`;
  const lastDigit = day % 10;
  switch (lastDigit) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};

export const formatDateDoMMM = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("Do MMMM");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const formatDateYMD = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("YYYY-MM-DD");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const getDateTimestamp = (
  date?: string | Date | null
): number | null => {
  if (!date) return null;

  const timestamp = dayjs(date).valueOf();
  return isNaN(timestamp) ? null : timestamp;
};

export const formatDateMMMMYYYY = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("MMMM YYYY");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const formatDateMMMDDYYYY = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("MMM DD, YYYY");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const formatDateMMMYY = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("MMM YY");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const formatDateMMDD = (date?: string | Date | null): string => {
  if (!date) return "-";

  const formatted = dayjs(date).format("MM/DD");
  return formatted === "Invalid Date" ? "-" : formatted;
};

export const formatCustomerBirthday = (
  customer: TCustomerModelJSON
): string => {
  if (customer.dateOfBirth) {
    return formatDateDoMMM(customer.dateOfBirth);
  }

  const { dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear } = customer;

  if (dateOfBirthDay && dateOfBirthMonth) {
    const monthName = dayjs()
      .month(dateOfBirthMonth - 1)
      .format("MMMM");
    const dayWithSuffix = getOrdinalSuffix(dateOfBirthDay);
    return dateOfBirthYear
      ? `${dayWithSuffix} ${monthName}, ${dateOfBirthYear}`
      : `${dayWithSuffix} ${monthName}`;
  }

  return "Not available";
};

export const getDateRange = (campaignData: TCampaignModelJSON) => {
  const fromDate = campaignData ? new Date(campaignData.createdOn) : new Date();
  return { fromDate: fromDate.toISOString().split("T")[0] };
};

export const formatUTCDateTime = (
  date?: string | Date | null,
  format: string = "MMM DD, YYYY, h:mm A"
): string => {
  if (!date) return "-";

  const formatted = dayjs.utc(date).format(format);
  return formatted === "Invalid Date" ? "-" : formatted;
};
